/**
 * Central configuration for category routes
 * Single source of truth for category UUIDs, URL paths, and display names
 * 
 * This file consolidates mappings previously scattered across:
 * - src/utils/supplierCategoryMapping.ts
 * - src/utils/formCategoryMapping.ts
 * - src/utils/categoryMapping.ts
 * - src/utils/pageTypeMapping.ts
 */

// Category UUID → URL path (without leading slash for consistency)
// UUIDs are from supplier_categories table where sub_category = true
export const CATEGORY_ROUTES: Record<string, string> = {
  // Venues
  '7f8a6ada-dac7-4aee-85db-2ef0cb00ae19': 'brollopslokaler',       // Bröllopslokaler

  // Photo & Video
  'dbd035c6-08ef-45dd-8fd2-67cabcd21368': 'brollopsfotografer',    // Bröllopsfotograf
  '53d9f762-4c13-49bd-9ea2-86c45d38e98e': 'brollopsvideografer',  // Videograf

  // Fashion & Beauty
  '9f712571-d29a-4f16-977a-49be46028845': 'brollopsklanningar',   // Brudklänning
  'b5e84a2c-4a4a-48b6-b29b-c0131f27905a': 'brollopsklader-herr',  // Bröllopskostym
  'fc55d726-5495-4924-a877-f2ca453996ef': 'makeup',               // Brudmakeup
  'c10191cf-9580-412f-a153-1049c9d4cd94': 'bruduppsattning-frisorer',  // Brudfrisyrer
  '97405fad-d71d-4348-9e08-371f62059ecc': 'vigselringar',         // Vigselringar

  // Food & Drinks
  'b1529972-45f6-46d0-9429-9263ab35c443': 'brollopstartor',       // Bröllopstårta
  '6a72557f-e7ff-4b58-b40a-7ce95e918d2c': 'catering-brollop',     // Catering

  // Flowers & Decoration
  '8b19e068-5f3a-4c58-b529-12cac48a8f1a': 'brollopsbuketter',     // Brudbukett

  // Entertainment & Music
  'eb24052c-f9dc-4ad8-8616-1f9005868f9c': 'musik-brollop',        // Musik
  'e38739b0-436b-4c7a-ac15-f34a67944b2e': 'underhallning-brollop', // Underhållning
  /** Fotobås (supplier_categories.id; matches supplierInputFieldConfiguration) */
  '087527a2-e38b-4daf-839c-dc0d7d1e11c2': 'fotobas-brollop',

  // Services & Equipment
  '3c950b62-9b53-47f4-8ff0-35514153d7f1': 'brollopsplanerare',    // Bröllopsplanerare
  '209127fe-e949-4cd8-82f7-3250fd01f56f': 'brollopskort',         // Bröllopskort
  '65d7d265-9107-48ae-911a-477a4002b18a': 'uthyrning-brollop',    // Uthyrning
  'bf3246a3-6085-4f3f-ac13-9fc47bd689f7': 'brollopsbilar',        // Bröllopsbil
};

/** supplier_categories.id for Fotobås (same key as in CATEGORY_ROUTES) */
export const FOTO_BAS_SUB_CATEGORY_ID = '087527a2-e38b-4daf-839c-dc0d7d1e11c2';

// Reverse lookup: URL path → UUID
export const ROUTE_TO_CATEGORY_ID: Record<string, string> = Object.entries(
  CATEGORY_ROUTES
).reduce(
  (acc, [uuid, path]) => {
    acc[path] = uuid;
    return acc;
  },
  {} as Record<string, string>
);

// Swedish display names for UI
export const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  'brollopslokaler': 'Bröllopslokaler',
  'brollopsfotografer': 'Bröllopsfotograf',
  'brollopsvideografer': 'Videograf',
  'brollopsklanningar': 'Brudklänning',
  'brollopsklader-herr': 'Bröllopskostym',
  'makeup': 'Makeupartist',
  'bruduppsattning-frisorer': 'Frisör',
  'vigselringar': 'Vigselringar',
  'brollopstartor': 'Bröllopstårta',
  'catering-brollop': 'Catering',
  'brollopsbuketter': 'Florist',
  'musik-brollop': 'Musik',
  'underhallning-brollop': 'Underhållning',
  'fotobas-brollop': 'Fotobås',
  'brollopsplanerare': 'Bröllopsplanerare',
  'brollopskort': 'Bröllopskort',
  'uthyrning-brollop': 'Uthyrning',
  'brollopsbilar': 'Bröllopstransport',
};

/** Map DB names to display names (display-only rename, no DB change) */
export const CATEGORY_DISPLAY_OVERRIDE: Record<string, string> = {
  'Brudbukett': 'Florist',
  'Brudfrisyrer': 'Frisör',
  'Brudmakeup': 'Makeupartist',
  'Bröllopsbil': 'Bröllopstransport',
};

export const getDisplayNameForDbCategory = (dbName: string): string =>
  CATEGORY_DISPLAY_OVERRIDE[dbName] ?? dbName;

// Named path constants for convenient imports
// Using literal types to ensure compatibility with VenueGrid pageType
export const CATEGORY_PATHS = {
  VENUES: 'brollopslokaler' as const,
  PHOTOGRAPHERS: 'brollopsfotografer' as const,
  VIDEOGRAPHERS: 'brollopsvideografer' as const,
  DRESSES: 'brollopsklanningar' as const,
  MENSWEAR: 'brollopsklader-herr' as const,
  MAKEUP: 'makeup' as const,
  HAIRDRESSER: 'bruduppsattning-frisorer' as const,
  RINGS: 'vigselringar' as const,
  CAKES: 'brollopstartor' as const,
  CATERING: 'catering-brollop' as const,
  BOUQUETS: 'brollopsbuketter' as const,
  MUSIC: 'musik-brollop' as const,
  ENTERTAINMENT: 'underhallning-brollop' as const,
  FOTO_BAS: 'fotobas-brollop' as const,
  PLANNERS: 'brollopsplanerare' as const,
  CARDS: 'brollopskort' as const,
  RENTALS: 'uthyrning-brollop' as const,
  CARS: 'brollopsbilar' as const,
};

// ============ Page Types for VenueGrid ============
// Using 'as const' to provide both runtime values and TypeScript literal types
export const PAGE_TYPES = {
  VENUES: CATEGORY_PATHS.VENUES.toString(),
  PHOTOGRAPHERS: CATEGORY_PATHS.PHOTOGRAPHERS.toString(),
  VIDEOGRAPHERS: CATEGORY_PATHS.VIDEOGRAPHERS.toString(),
  WEDDING_CARDS: CATEGORY_PATHS.CARDS.toString(),
  WEDDING_DRESSES: CATEGORY_PATHS.DRESSES.toString(),
  MENSWEAR: CATEGORY_PATHS.MENSWEAR.toString(),
  WEDDING_CAKES: CATEGORY_PATHS.CAKES.toString(),
  CATERING: CATEGORY_PATHS.CATERING.toString(),
  BRIDAL_BOUQUETS: CATEGORY_PATHS.BOUQUETS.toString(),
  MUSIC: CATEGORY_PATHS.MUSIC.toString(),
  ENTERTAINMENT: CATEGORY_PATHS.ENTERTAINMENT.toString(),
  FOTO_BAS: CATEGORY_PATHS.FOTO_BAS.toString(),
  UTHYRNING: CATEGORY_PATHS.RENTALS.toString(),
  BROLLOPSBILAR: CATEGORY_PATHS.CARS.toString(),
  WEDDING_RINGS: CATEGORY_PATHS.RINGS.toString(),
  HAIRDRESSER: CATEGORY_PATHS.HAIRDRESSER.toString(),
  MAKEUP: CATEGORY_PATHS.MAKEUP.toString(),
  WEDDING_PLANNERS: CATEGORY_PATHS.PLANNERS.toString(),
  OTHER: 'other'
} as const;

// Derive the PageType union from the constant
export type PageType = typeof PAGE_TYPES[keyof typeof PAGE_TYPES];

// ============ Helper Functions ============

/**
 * Get the URL path for a category UUID
 * @param categoryId - The category UUID
 * @returns The URL path (without leading slash) or null if not found
 */
export const getCategoryPath = (categoryId: string): string | null => {
  return CATEGORY_ROUTES[categoryId] || null;
};

/**
 * Get the category UUID for a URL path
 * @param path - The URL path (with or without leading slash)
 * @returns The category UUID or null if not found
 */
export const getCategoryIdFromPath = (path: string): string | null => {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return ROUTE_TO_CATEGORY_ID[cleanPath] || null;
};

/**
 * Get the Swedish display name for a category path
 * @param path - The URL path (with or without leading slash)
 * @returns The Swedish display name or 'Okänd kategori' if not found
 */
export const getCategoryDisplayName = (path: string): string => {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return CATEGORY_DISPLAY_NAMES[cleanPath] || 'Okänd kategori';
};

/**
 * Get the Swedish display name for a category UUID
 * @param categoryId - The category UUID
 * @returns The Swedish display name or 'Okänd kategori' if not found
 */
export const getCategoryDisplayNameById = (categoryId: string): string => {
  const path = CATEGORY_ROUTES[categoryId];
  return path ? CATEGORY_DISPLAY_NAMES[path] || 'Okänd kategori' : 'Okänd kategori';
};

/**
 * Get all category paths (useful for sitemap, navigation, etc.)
 * @returns Array of all URL paths (without leading slash)
 */
export const getAllCategoryPaths = (): string[] => {
  return Object.values(CATEGORY_ROUTES);
};

/**
 * Get all category IDs
 * @returns Array of all category UUIDs
 */
export const getAllCategoryIds = (): string[] => {
  return Object.keys(CATEGORY_ROUTES);
};

/**
 * Check if a path is a valid category path
 * @param path - The URL path to check (with or without leading slash)
 * @returns true if the path is a valid category
 */
export const isValidCategoryPath = (path: string): boolean => {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return cleanPath in ROUTE_TO_CATEGORY_ID;
};

/**
 * Check if a UUID is a valid category ID
 * @param categoryId - The UUID to check
 * @returns true if the UUID is a valid category
 */
export const isValidCategoryId = (categoryId: string): boolean => {
  return categoryId in CATEGORY_ROUTES;
};

// ============ Registration form (single source of truth for category dropdown) ============

/** Options for registration category dropdown: value = UUID, label = display name */
export const REGISTRATION_CATEGORY_OPTIONS: { value: string; label: string }[] = Object.entries(
  CATEGORY_ROUTES
).map(([uuid, path]) => ({
  value: uuid,
  label: CATEGORY_DISPLAY_NAMES[path] ?? path,
}));

/** Shape used by SupplierSignupForm for grouped category dropdown */
export interface RegistrationCategoryForForm {
  id: string;
  name: string;
  category: boolean;
  sub_category: boolean;
  category_number: number;
}

/** Flat list of categories for registration (config-driven UUIDs) */
export function getRegistrationCategories(): RegistrationCategoryForForm[] {
  return Object.entries(CATEGORY_ROUTES).map(([uuid, path]) => ({
    id: uuid,
    name: CATEGORY_DISPLAY_NAMES[path] ?? path,
    category: false,
    sub_category: true,
    category_number: 0,
  }));
}

/** Grouped shape for SupplierSignupForm (single group, all subcategories) */
export function getRegistrationGroupedCategories(): Record<
  number,
  { main: RegistrationCategoryForForm | null; subCategories: RegistrationCategoryForForm[] }
> {
  const subCategories = getRegistrationCategories();
  return { 0: { main: null, subCategories } };
}

// ============ Location Page Routes ============
// These are landing/discovery pages that help users find suppliers by location
// Separate from the main category browse pages (e.g., /brollopslokaler vs /wedding-locations)

export const LOCATION_PAGE_ROUTES = {
  VENUES_LOCATIONS: 'brollopstjanster/brollopslokaler' as const,
  PHOTOGRAPHERS_LOCATIONS: 'brollopstjanster/brollopsfotografer' as const,
  VIDEOGRAPHERS_LOCATIONS: 'brollopstjanster/brollopsvideografer' as const,
  DRESSES_LOCATIONS: 'brollopstjanster/brollopsklanningar' as const,
  MENSWEAR_LOCATIONS: 'brollopstjanster/brollopsklader-herr' as const,
  MAKEUP_LOCATIONS: 'brollopstjanster/makeup' as const,
  HAIRDRESSER_LOCATIONS: 'brollopstjanster/bruduppsattning-frisorer' as const,
  RINGS_LOCATIONS: 'brollopstjanster/vigselringar' as const,
  CAKES_LOCATIONS: 'brollopstjanster/brollopstartor' as const,
  CATERING_LOCATIONS: 'brollopstjanster/catering-brollop' as const,
  BOUQUETS_LOCATIONS: 'brollopstjanster/brollopsbuketter' as const,
  MUSIC_LOCATIONS: 'brollopstjanster/musik-brollop' as const,
  ENTERTAINMENT_LOCATIONS: 'brollopstjanster/underhallning-brollop' as const,
  PLANNERS_LOCATIONS: 'brollopstjanster/brollopsplanerare' as const,
  CARDS_LOCATIONS: 'brollopstjanster/brollopskort' as const,
  RENTALS_LOCATIONS: 'brollopstjanster/uthyrning-brollop' as const,
  CARS_LOCATIONS: 'brollopstjanster/brollopsbilar' as const,
  ALL_VENUES: 'all-wedding-venues' as const,
} as const;

/**
 * Get location page path with leading slash
 * @param key - The key from LOCATION_PAGE_ROUTES
 * @returns The URL path with leading slash
 */
export const getLocationPagePath = (key: keyof typeof LOCATION_PAGE_ROUTES): string => {
  return `/${LOCATION_PAGE_ROUTES[key]}`;
};
